import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../api/api";
import { BACKEND_URL } from "../api/api";
import toast from "react-hot-toast";
import { documentPaths } from "../utils/documents";
import { contactErrors, vatError } from "../utils/contactValidation";

const emptyCustomer = {
  firstName: "",
  lastName: "",
  address: "",
  streetName: "",
  houseNumber: "",
  busNumber: "",
  postalCode: "",
  city: "",
  phone: "",
  email: "",
  wantsInvoice: false,
  vatNumber: "",
  documents: "",
  notes: ""
};

const parseDocuments = (value) => {
  try {
    const parsed = JSON.parse(value || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const removeDocumentPath = (value, pathToRemove) => {
  const docs = parseDocuments(value);
  const remove = (item) => {
    if (Array.isArray(item)) return item.filter((value) => value !== pathToRemove).map(remove);
    if (item && typeof item === "object") {
      return Object.fromEntries(Object.entries(item).map(([key, value]) => [key, remove(value)]));
    }
    return item === pathToRemove ? null : item;
  };

  return JSON.stringify(remove(docs));
};

const buildAddress = (form) => [
  form.streetName,
  form.houseNumber,
  form.busNumber ? `bus ${form.busNumber}` : "",
  form.postalCode,
  form.city
].filter(Boolean).join(" ");

export default function Customers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [isCreating, setIsCreating] = useState(true);
  const [form, setForm] = useState(emptyCustomer);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const validationErrors = contactErrors(form.phone, form.email);
  const vatValidationError = vatError(form.vatNumber);

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => (await API.get("/customers")).data
  });

  const { data: reservations = [] } = useQuery({
    queryKey: ["reservations"],
    queryFn: async () => (await API.get("/reservations")).data
  });

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return customers.filter((customer) => (
      `${customer.firstName || ""} ${customer.lastName}`.toLowerCase().includes(term) ||
      (customer.phone || "").toLowerCase().includes(term)
    ));
  }, [customers, search]);

  const selected = customers.find((customer) => customer.id === selectedId);
  const history = selected
    ? reservations.filter((reservation) => (
      reservation.email && selected.email && reservation.email.toLowerCase() === selected.email.toLowerCase()
    ))
    : [];

  useEffect(() => {
    if (isCreating) return;

    if (!selectedId) return;

    if (!selected && customers.length) {
      setSelectedId(customers[0].id);
      return;
    }

    if (!selected && !customers.length) {
      setSelectedId(null);
      setIsCreating(true);
      setForm(emptyCustomer);
    }
  }, [customers, selected, selectedId, isCreating]);

  useEffect(() => {
    if (isCreating || !selected) return;

    setForm({
      firstName: selected.firstName || "",
      lastName: selected.lastName || "",
      address: selected.address || "",
      streetName: selected.streetName || "",
      houseNumber: selected.houseNumber || "",
      busNumber: selected.busNumber || "",
      postalCode: selected.postalCode || "",
      city: selected.city || "",
      phone: selected.phone || "",
      email: selected.email || "",
      wantsInvoice: Boolean(selected.wantsInvoice),
      vatNumber: selected.vatNumber || "",
      documents: selected.documents || "",
      notes: selected.notes || ""
    });
  }, [selected, isCreating]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const errors = contactErrors(payload.phone, payload.email);
      if (errors.phone || errors.email) {
        throw new Error(errors.phone || errors.email);
      }

      if (payload.vatNumber && vatError(payload.vatNumber)) {
        throw new Error(vatError(payload.vatNumber));
      }

      if (documentFiles.length > 5) {
        throw new Error("Je kan maximaal 5 documenten uploaden.");
      }

      if (documentPaths(payload.documents).length + documentFiles.length > 5) {
        throw new Error("Je kan maximaal 5 documenten uploaden.");
      }

      const basePayload = {
        ...payload,
        address: buildAddress(payload) || payload.address
      };

      const savedCustomer = !isCreating && selected
        ? (await API.patch(`/customers/${selected.id}`, basePayload)).data
        : (await API.post("/customers", basePayload)).data;

      if (!documentFiles.length) {
        return { data: savedCustomer };
      }

      try {
        const uploadedPaths = [];

        for (const file of documentFiles) {
          const formData = new FormData();
          formData.append("image", file);
          const res = await API.post("/upload", formData);
          uploadedPaths.push(res.data.filePath);
        }

        const existingDocs = parseDocuments(savedCustomer.documents || basePayload.documents);
        const nextDocuments = JSON.stringify({
          ...existingDocs,
          adminDocs: [
            ...(existingDocs.adminDocs || []),
            ...uploadedPaths
          ]
        });

        const updatedCustomer = (await API.patch(`/customers/${savedCustomer.id}`, {
          documents: nextDocuments
        })).data;

        return { data: updatedCustomer };
      } catch {
        return { data: savedCustomer, documentWarning: true };
      }
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setSelectedId(res.data.id);
      setIsCreating(false);
      setDocumentFiles([]);
      if (res.documentWarning) {
        toast.error("Klant opgeslagen, maar documenten niet volledig geüpload");
        return;
      }
      toast.success("Klant opgeslagen");
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.error(error.response?.data?.error || error.message || "Klant opslaan mislukt");
    }
  });

  const archiveMutation = useMutation({
    mutationFn: (id) => API.post(`/customers/${id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      setSelectedId(null);
      setIsCreating(true);
      setForm(emptyCustomer);
      setDocumentFiles([]);
      toast.success("Klant gearchiveerd");
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.error(error.response?.data?.error || "Klant archiveren mislukt");
    }
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: ({ id, path }) => API.delete(`/customers/${id}/documents`, { data: { path } }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setForm((current) => ({
        ...current,
        documents: res.data.documents || removeDocumentPath(current.documents, res.data.path)
      }));
      toast.success("Document verwijderd");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Document verwijderen mislukt");
    }
  });

  const documentImages = useMemo(() => {
    return documentPaths(form.documents);
  }, [form.documents]);

  const selectCustomer = (customer) => {
    setIsCreating(false);
    setSelectedId(customer.id);
    setForm({
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      address: customer.address || "",
      streetName: customer.streetName || "",
      houseNumber: customer.houseNumber || "",
      busNumber: customer.busNumber || "",
      postalCode: customer.postalCode || "",
      city: customer.city || "",
      phone: customer.phone || "",
      email: customer.email || "",
      wantsInvoice: Boolean(customer.wantsInvoice),
      vatNumber: customer.vatNumber || "",
      documents: customer.documents || "",
      notes: customer.notes || ""
    });
    setDocumentFiles([]);
  };

  const selectDocumentFiles = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const imageFiles = selectedFiles.filter((file) => file.type.startsWith("image/"));
    const nextFiles = [...documentFiles, ...imageFiles].slice(0, 5);

    if (selectedFiles.length !== imageFiles.length) {
      toast.error("Gebruik enkel afbeeldingen of foto's.");
    }

    if (documentFiles.length + imageFiles.length > 5) {
      toast.error("Je kan maximaal 5 documenten uploaden.");
    }

    setDocumentFiles(nextFiles);
    event.target.value = "";
  };

  const removeDocumentFile = (index) => {
    setDocumentFiles(documentFiles.filter((_, fileIndex) => fileIndex !== index));
  };

  const removeExistingDocument = (path) => {
    if (!window.confirm("Deze foto verwijderen?")) return;
    if (!isCreating && selectedId) {
      deleteDocumentMutation.mutate({ id: selectedId, path });
      return;
    }
    setForm({ ...form, documents: removeDocumentPath(form.documents, path) });
    toast.success("Document verwijderd. Klik op opslaan om te bewaren.");
  };

  if (isLoading) return <div className="loading-state">Klanten laden...</div>;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Klanten</h1>
          <p className="page-subtitle">Zoek klanten, maak ze manueel aan en bekijk hun reservatiehistoriek.</p>
        </div>
        <button className="button secondary" onClick={() => { setSelectedId(null); setIsCreating(true); setForm(emptyCustomer); setDocumentFiles([]); }}>
          Nieuwe klant
        </button>
      </header>

      <div className="panel panel-pad">
        <input className="input" placeholder="Zoek op naam of telefoonnummer" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="reservation-workbench">
        <div className="list">
          {filtered.map((customer) => (
            <button key={customer.id} className={`list-row reservation-row${selectedId === customer.id ? " selected" : ""}`} onClick={() => selectCustomer(customer)}>
              <div>
                <div className="row-title">{customer.firstName} {customer.lastName}</div>
                <div className="row-meta">{customer.phone || "-"} - {customer.email || "-"}</div>
              </div>
            </button>
          ))}
        </div>

        <aside className="panel panel-pad detail-panel">
          <div className="detail-section">
            <h2 className="page-title" style={{ fontSize: 24 }}>{!isCreating && selected ? "Klant bewerken" : "Nieuwe klant"}</h2>
          </div>
          <div className="detail-section">
            <div className="form-grid">
              <label className="field"><span className="field-label">Voornaam</span><input className="input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></label>
              <label className="field"><span className="field-label">Naam</span><input className="input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></label>
              <label className="field"><span className="field-label">Telefoonnummer</span><input className="input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />{validationErrors.phone && <span className="field-error">{validationErrors.phone}</span>}</label>
              <label className="field"><span className="field-label">E-mail</span><input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />{validationErrors.email && <span className="field-error">{validationErrors.email}</span>}</label>
            </div>
          </div>

          <div className="detail-section">
            <h3 className="detail-title">Adres</h3>
            <div className="form-grid">
              <label className="field"><span className="field-label">Straatnaam</span><input className="input" value={form.streetName} onChange={(e) => setForm({ ...form, streetName: e.target.value })} /></label>
              <label className="field"><span className="field-label">Huisnummer</span><input className="input" value={form.houseNumber} onChange={(e) => setForm({ ...form, houseNumber: e.target.value })} /></label>
              <label className="field"><span className="field-label">Busnummer optioneel</span><input className="input" value={form.busNumber} onChange={(e) => setForm({ ...form, busNumber: e.target.value })} /></label>
              <label className="field"><span className="field-label">Postcode</span><input className="input" value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} /></label>
              <label className="field"><span className="field-label">Gemeente</span><input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></label>
            </div>
          </div>

          <div className="detail-section">
            <h3 className="detail-title">Facturatie</h3>
            <label className="check-field">
              <input type="checkbox" checked={form.wantsInvoice} onChange={(e) => setForm({ ...form, wantsInvoice: e.target.checked, vatNumber: e.target.checked ? form.vatNumber : "" })} />
              <span>Klant wenst een factuur</span>
            </label>
            {form.wantsInvoice && (
              <label className="field" style={{ marginTop: 12 }}>
                <span className="field-label">BTW-nummer</span>
                <input className="input" value={form.vatNumber} onChange={(e) => setForm({ ...form, vatNumber: e.target.value })} />
                {vatValidationError && <span className="field-error">{vatValidationError}</span>}
              </label>
            )}
          </div>

          <div className="detail-section">
            <h3 className="detail-title">Documenten</h3>
            <p className="page-subtitle">Documenten zijn optioneel en kunnen later nog toegevoegd worden.</p>
            <input
              className="input"
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              multiple
              onChange={selectDocumentFiles}
            />
            {documentFiles.length > 0 && (
              <div className="document-upload-list">
                {documentFiles.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="document-file-row">
                    <span>{file.name}</span>
                    <button type="button" className="button secondary" onClick={() => removeDocumentFile(index)}>
                      Verwijderen
                    </button>
                  </div>
                ))}
              </div>
            )}
            {documentImages.length > 0 && (
              <div className="photo-grid" style={{ marginTop: 12 }}>
                {documentImages.map((path) => (
                  <div key={path} className="photo-thumb-wrap">
                    <button type="button" className="checkout-admin-photo" onClick={() => setPreviewImage(path)}>
                    <img className="photo-thumb" src={`${BACKEND_URL}${path}`} alt="Document" />
                    </button>
                    <button type="button" className="photo-delete-button" onClick={() => removeExistingDocument(path)} aria-label="Foto verwijderen">x</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="detail-section">
            <label className="field" style={{ marginTop: 12 }}><span className="field-label">Extra notities</span><textarea className="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          </div>
          <div className="detail-section">
            <div className="action-row">
              <button className="button" onClick={() => saveMutation.mutate(form)}>Opslaan</button>
              {!isCreating && selected && (
                <>
                  <button className="button secondary" onClick={() => navigate(`/customers/${selected.id}`)}>Dossier openen</button>
                  <button
                    className="button danger"
                    onClick={() => {
                      if (window.confirm("Deze klant archiveren? De klant verdwijnt uit de actieve lijst.")) {
                        archiveMutation.mutate(selected.id);
                      }
                    }}
                  >
                    Archiveren
                  </button>
                </>
              )}
            </div>
          </div>
          {selected && (
            <div className="detail-section">
              <h3 className="detail-title">Reservatiehistoriek</h3>
              <div className="list">
                {history.length === 0 && <div className="empty-state">Geen reservaties gevonden op e-mail.</div>}
                {history.map((reservation) => (
                  <button
                    key={reservation.id}
                    className="list-row reservation-row"
                    onClick={() => navigate(`/reservations?reservationId=${reservation.id}`)}
                  >
                    <div>
                      <div className="row-title">{reservation.trailer?.name || `Trailer #${reservation.trailerId}`}</div>
                      <div className="row-meta">{new Date(reservation.startDate).toLocaleDateString("nl-BE")} tot {new Date(reservation.endDate).toLocaleDateString("nl-BE")} - {reservation.status}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
      {previewImage && (
        <button className="image-modal" onClick={() => setPreviewImage(null)}>
          <img src={`${BACKEND_URL}${previewImage}`} alt="Document groot" />
        </button>
      )}
    </section>
  );
}
